import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile as firebaseUpdateProfile,
  setPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const auth = getAuth();

  // ضبط نوع تخزين الجلسة: SESSION يعني الجلسة بتفضل شغالة طول ما التاب/المتصفح مفتوح
  // وبمجرد ما تقفل المتصفح أو التاب، بيتم تسجيل الخروج تلقائياً ولازم تسجل دخول تاني
  useEffect(() => {
    setPersistence(auth, browserSessionPersistence).catch((error) => {
      console.error('Persistence setup error:', error);
    });
  }, [auth]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          
          let userData;
          if (userDoc.exists()) {
            userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              emailVerified: firebaseUser.emailVerified,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              ...userDoc.data()
            };
          } else {
            const newUserData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || 'User',
              role: 'admin',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            await setDoc(doc(db, 'users', firebaseUser.uid), newUserData);
            userData = { ...firebaseUser, ...newUserData };
          }
          
          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData));
          
          const token = await firebaseUser.getIdToken();
          localStorage.setItem('adminToken', token);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('adminToken');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  const login = async (email, password) => {
    try {
      setLoading(true);

      // التأكيد على نوع الجلسة قبل تسجيل الدخول مباشرة
      await setPersistence(auth, browserSessionPersistence);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      let userData;
      if (!userDoc.exists()) {
        const newUserData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || 'Admin',
          role: 'admin',
          createdAt: new Date().toISOString()
        };
        await setDoc(doc(db, 'users', firebaseUser.uid), newUserData);
        userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          ...newUserData
        };
      } else {
        userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          ...userDoc.data()
        };
      }
      
      const token = await firebaseUser.getIdToken();
      
      setUser(userData);
      setIsAuthenticated(true);
      
      localStorage.setItem('adminToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        lastLogin: new Date().toISOString()
      });
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'فشل تسجيل الدخول';
      
      if (error.code === 'auth/user-not-found') errorMessage = 'المستخدم غير موجود';
      else if (error.code === 'auth/wrong-password') errorMessage = 'كلمة المرور غير صحيحة';
      else if (error.code === 'auth/invalid-email') errorMessage = 'البريد الإلكتروني غير صالح';
      else if (error.code === 'auth/user-disabled') errorMessage = 'تم تعطيل هذا الحساب';
      else if (error.code === 'auth/too-many-requests') errorMessage = 'محاولات كثيرة. حاول لاحقاً';
      else if (error.code === 'auth/invalid-credential') errorMessage = 'البريد أو كلمة المرور غير صحيحة';
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('user');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      if (updates.displayName || updates.photoURL) {
        await firebaseUpdateProfile(auth.currentUser, {
          displayName: updates.displayName || user.displayName,
          photoURL: updates.photoURL || user.photoURL
        });
      }
      
      await updateDoc(doc(db, 'users', user.uid), {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      await signInWithEmailAndPassword(auth, user.email, currentPassword);
      await updatePassword(auth.currentUser, newPassword);
      
      return { success: true, message: 'تم تغيير كلمة المرور بنجاح' };
    } catch (error) {
      console.error('Change password error:', error);
      
      let errorMessage = 'فشل تغيير كلمة المرور';
      if (error.code === 'auth/wrong-password') errorMessage = 'كلمة المرور الحالية غير صحيحة';
      else if (error.code === 'auth/weak-password') errorMessage = 'كلمة المرور الجديدة ضعيفة';
      
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'تم إرسال رابط إعادة تعيين كلمة المرور' };
    } catch (error) {
      console.error('Reset password error:', error);
      
      let errorMessage = 'فشل إرسال رابط إعادة التعيين';
      if (error.code === 'auth/user-not-found') errorMessage = 'المستخدم غير موجود';
      else if (error.code === 'auth/invalid-email') errorMessage = 'البريد الإلكتروني غير صالح';
      
      return { success: false, error: errorMessage };
    }
  };

  const refreshToken = async () => {
    try {
      if (!auth.currentUser) return false;
      const token = await auth.currentUser.getIdToken(true);
      localStorage.setItem('adminToken', token);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
      return false;
    }
  };

  const isAdmin = () => user && (user.role === 'admin' || user.admin === true);

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin: isAdmin(),
    login,
    logout,
    updateProfile,
    changePassword,
    resetPassword,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;